import React from 'react';

function getRandomBetween(min, max) {
  const safeMin = Math.max(0, Math.min(100, Number.isFinite(min) ? min : 0));
  const safeMaxBase = Math.max(safeMin, Math.min(100, Number.isFinite(max) ? max : safeMin));
  const safeMax = safeMaxBase >= safeMin ? safeMaxBase : safeMin;
  const span = safeMax - safeMin;
  if (span <= 0) return safeMin;
  return safeMin + Math.floor(Math.random() * (span + 1));
}

/**
 * Provides a numeric progress % for UI.
 *
 * - If `realProgress` is a finite number, uses it (clamped 0-100).
 * - Otherwise, when `visible` is true, fakes progress at 2%/sec up to 98%.
 * - When `visible` turns false, snaps to 100% and keeps rendering briefly,
 *   then returns `renderVisible=false` so callers can unmount the overlay.
 */
export default function useFakeProgress({
  visible,
  realProgress,
  fakeRatePerSecond = 1,
  fakeCap = 98,
  finishAnimationMs = 2000,
  finishDelayMs = 150,
  onExitComplete,
}) {
  const hasReal = typeof realProgress === 'number' && Number.isFinite(realProgress);
  const clampedReal = hasReal ? Math.max(0, Math.min(100, realProgress)) : undefined;

  const minInitialFakeProgress = 5;
  const maxInitialFakeProgress = 10;

  const [renderVisible, setRenderVisible] = React.useState(Boolean(visible));
  const [progress, setProgress] = React.useState(() => {
    if (hasReal) return clampedReal ?? 0;
    if (visible) {
      return getRandomBetween(minInitialFakeProgress, maxInitialFakeProgress);
    }
    return 0;
  });

  const intervalRef = React.useRef(null);
  const finishIntervalRef = React.useRef(null);
  const finishTimeoutRef = React.useRef(null);
  const progressRef = React.useRef(progress);
  const onExitCompleteRef = React.useRef(onExitComplete);
  React.useEffect(() => {
    onExitCompleteRef.current = onExitComplete;
  }, [onExitComplete]);

  React.useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const clearTimers = React.useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    if (finishIntervalRef.current) clearInterval(finishIntervalRef.current);
    finishIntervalRef.current = null;
    if (finishTimeoutRef.current) clearTimeout(finishTimeoutRef.current);
    finishTimeoutRef.current = null;
  }, []);

  // Keep in sync with real progress when provided.
  React.useEffect(() => {
    if (!hasReal) return;
    setProgress(clampedReal);
  }, [hasReal, clampedReal]);

  React.useEffect(() => {
    clearTimers();

    if (visible) {
      setRenderVisible(true);

      // For purely "fake" progress (no realProgress provided), ensure we never
      // start from 0% when the overlay first appears. Instead, jump to a small
      // random value between 5–10% and then continue faking from there.
      if (!hasReal) {
        setProgress((prev) => {
          const safePrev = typeof prev === 'number' && Number.isFinite(prev) ? prev : 0;
          if (safePrev > 0) return safePrev;
          const initial = getRandomBetween(minInitialFakeProgress, maxInitialFakeProgress);
          progressRef.current = initial;
          return initial;
        });
      }

      // If we have real progress, don't fake.
      if (hasReal) return () => clearTimers();

      // Fake at 2%/sec => +1 every 500ms by default.
      const stepMs = 500;
      const step = (fakeRatePerSecond * stepMs) / 1000;
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = Math.min(fakeCap, prev + step);
          return next;
        });
      }, stepMs);

      return () => clearTimers();
    }

    // visible is false => finish to 100% then hide.
    if (renderVisible) {
      const start = Math.max(0, Math.min(100, progressRef.current || 0));
      const duration = Math.max(0, Number(finishAnimationMs) || 0);

      if (start >= 100 || duration === 0) {
        setProgress(100);
        finishTimeoutRef.current = setTimeout(() => {
          setRenderVisible(false);
          onExitCompleteRef.current?.();
        }, finishDelayMs);
        return () => clearTimers();
      }

      const stepMs = 50;
      const startedAt = Date.now();
      finishIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const t = Math.min(1, elapsed / duration);
        const val = start + (100 - start) * t;
        setProgress(val);
        if (t >= 1) {
          clearInterval(finishIntervalRef.current);
          finishIntervalRef.current = null;
          setProgress(100);
          finishTimeoutRef.current = setTimeout(() => {
            setRenderVisible(false);
            onExitCompleteRef.current?.();
          }, finishDelayMs);
        }
      }, stepMs);
    } else {
      setProgress(0);
    }

    return () => clearTimers();
  }, [
    visible,
    renderVisible,
    hasReal,
    clearTimers,
    fakeRatePerSecond,
    fakeCap,
    finishAnimationMs,
    finishDelayMs,
  ]);

  return {
    renderVisible,
    progress,
    hasRealProgress: hasReal,
  };
}
